<template>
    <div class="ui-page">
        <ui-page-header title="Agenda de visitas" :subtitle="data ? `Semana de ${formatShortDate(data.start)} a ${formatShortDate(data.end)} · cada agenda nasce de um caso e volta com resultado e feedback` : ''">
            <v-btn outlined @click="move(-7)"><v-icon small>mdi-chevron-left</v-icon> semana</v-btn>
            <v-btn outlined @click="goToday">Hoje</v-btn>
            <v-btn outlined @click="move(7)">semana <v-icon small>mdi-chevron-right</v-icon></v-btn>
            <v-btn outlined to="/prospeccao/rota"><v-icon left small>mdi-map-marker-path</v-icon>Roteiros</v-btn>
            <v-btn v-if="$allowed('create')" color="primary" depressed @click="openNew"><v-icon left small>mdi-plus</v-icon>Nova agenda</v-btn>
        </ui-page-header>

        <section v-if="data" class="pm-stats mb-5" aria-label="Indicadores das agendas">
            <div class="pm-stat">
                <span class="pm-stat__label">Agendas realizadas em {{ data.kpis.year }}</span>
                <span class="pm-stat__value">{{ data.kpis.done }}</span>
                <span class="pm-stat__hint">{{ data.kpis.by_person.map((item) => `${item.name} ${item.year}`).join(" · ") || "nenhuma ainda" }}</span>
            </div>
            <div class="pm-stat">
                <span class="pm-stat__label"><span class="ui-pill__dot ui-pill__dot--success"></span>Resultaram em nomeação</span>
                <span class="pm-stat__value">{{ data.kpis.won }}</span>
                <span class="pm-stat__hint">{{ data.kpis.won_rate }}% das agendas (PP ou AJ da BeJ)</span>
            </div>
            <div class="pm-stat">
                <span class="pm-stat__label"><span class="ui-pill__dot ui-pill__dot--warning"></span>Aguardando devolutiva</span>
                <span class="pm-stat__value">{{ data.kpis.awaiting }}</span>
                <span class="pm-stat__hint">agenda feita, AJ ainda pendente</span>
            </div>
        </section>

        <div v-if="data" class="pm-week">
            <div v-for="day in visibleDays" :key="day.date" class="pm-day" :class="{ 'pm-day--today': day.date === today }">
                <div class="pm-day__head">{{ weekday(day.date) }} {{ formatShortDate(day.date) }}</div>
                <button v-for="meeting in day.meetings" :key="meeting.id" type="button" class="pm-card" :class="`pm-card--${meeting.status}`" @click="openDetail(meeting)">
                    <div class="pm-card__time">{{ meeting.time }} · {{ meeting.mode_label.toLowerCase() }}</div>
                    <div class="pm-card__title">{{ meeting.cases.map((item) => item.company_name).join(", ") }}</div>
                    <div class="pm-card__meta">{{ [meeting.cases[0] && meeting.cases[0].city, meeting.cases[0] && meeting.cases[0].uf].filter(Boolean).join("/") }}</div>
                    <div class="pm-card__meta">{{ meeting.with_whom || (meeting.judge ? meeting.judge.name : "") }}<span v-if="meeting.attendees.length"> · {{ meeting.attendees.map((item) => item.name).join(", ") }}</span></div>
                    <div class="pm-card__meta">
                        <ui-badge v-if="meeting.status !== 'scheduled'" set="prospect-meeting" :status="meeting.status" />
                        <span v-else>{{ meeting.onepage_label.toLowerCase() }}</span>
                    </div>
                    <div v-if="meeting.trip" class="pm-card__trip" :title="`Gerada pelo roteiro ${meeting.trip.name}`"><v-icon x-small>mdi-map-marker-path</v-icon><span class="pm-card__trip-name">{{ meeting.trip.name }}</span></div>
                </button>
                <router-link v-for="deadline in day.deadlines" :key="`d-${deadline.case_id}`" :to="`/prospeccao/casos/${deadline.case_id}`" class="pm-deadline">
                    Prazo TCA · {{ deadline.company_name }}
                </router-link>
                <div v-if="!day.meetings.length && !day.deadlines.length" class="pm-empty">—</div>
            </div>
        </div>
        <p v-if="data && data.upcoming.length" class="ui-small ui-muted mt-3">
            Próximas: {{ data.upcoming.map((item) => `${formatShortDate(item.date)} ${item.time} · ${item.cases.map((entry) => entry.company_name).join(", ")} (${item.mode_label.toLowerCase()})`).join(" · ") }}
        </p>

        <ui-section title="Agendas realizadas · últimas" class="mt-5" description="O feedback de cada agenda alimenta a ficha do caso e a ficha do juiz.">
            <ui-empty v-if="!history.length" text="Nenhuma agenda realizada ainda." />
            <table v-else class="pm-table">
                <thead><tr><th>Data</th><th>Caso</th><th class="r">Passivo</th><th>Juízo</th><th>Modalidade</th><th>Responsáveis</th><th>Onepage</th><th>Resultado</th><th>Feedback do juízo</th></tr></thead>
                <tbody>
                    <tr v-for="meeting in history" :key="meeting.id" @click="openDetail(meeting)">
                        <td class="ui-nowrap">{{ formatFeedDate(meeting.date) }}</td>
                        <td><div v-for="item in meeting.cases" :key="item.id"><strong>{{ item.company_name }}</strong> <span class="ui-muted">· {{ item.uf }}</span></div></td>
                        <td class="r ui-nowrap">{{ formatMoneyShort(meeting.cases.reduce((sum, item) => sum + (item.amount || 0), 0)) }}</td>
                        <td>{{ meeting.court ? meeting.court.name : "—" }}<div class="ui-small ui-muted">{{ meeting.judge ? meeting.judge.name : "" }}</div></td>
                        <td>{{ meeting.mode_label }}</td>
                        <td>{{ meeting.attendees.map((item) => item.name).join(" e ") }}</td>
                        <td>{{ meeting.onepage_status === "none" ? "não" : "sim" }}</td>
                        <td>{{ meeting.result_label || "—" }}</td>
                        <td class="pm-feedback">{{ meeting.feedback || "—" }}</td>
                    </tr>
                </tbody>
            </table>
        </ui-section>

        <prospect-meeting-dialog v-model="dialog.open" :meeting="dialog.meeting" @saved="afterSave" />

        <v-dialog v-model="detail.open" max-width="600">
            <v-card v-if="detail.meeting" class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ formatFeedDate(detail.meeting.date) }} {{ detail.meeting.time }} · {{ detail.meeting.mode_label }}</h3>
                        <p class="ui-section-description"><ui-badge set="prospect-meeting" :status="detail.meeting.status" /> {{ detail.meeting.with_whom || (detail.meeting.judge ? detail.meeting.judge.name : "") }}</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <dl class="pm-dl">
                        <dt>Casos</dt>
                        <dd><div v-for="item in detail.meeting.cases" :key="item.id"><router-link :to="`/prospeccao/casos/${item.id}`">{{ item.company_name }}</router-link> <span class="ui-muted">· {{ item.uf }} · {{ formatMoneyShort(item.amount) }}</span></div></dd>
                        <dt>Responsáveis</dt><dd>{{ detail.meeting.attendees.map((item) => item.name).join(", ") || "—" }}</dd>
                        <dt>Onepage</dt><dd>{{ detail.meeting.onepage_label }}</dd>
                        <dt v-if="detail.meeting.location">Local</dt><dd v-if="detail.meeting.location">{{ detail.meeting.location }}</dd>
                        <dt v-if="detail.meeting.trip">Roteiro</dt>
                        <dd v-if="detail.meeting.trip"><router-link :to="`/prospeccao/rota/${detail.meeting.trip.id}`">{{ detail.meeting.trip.name }}</router-link> <span class="ui-muted">· data e horário vêm do roteiro; ao gerar de novo, a agenda é atualizada</span></dd>
                        <dt v-if="detail.meeting.notes">Observações</dt><dd v-if="detail.meeting.notes" style="white-space: pre-line;">{{ detail.meeting.notes }}</dd>
                        <template v-if="detail.meeting.status === 'done'">
                            <dt>Resultado</dt><dd>{{ detail.meeting.result_label || "—" }}</dd>
                            <dt>Feedback</dt><dd>{{ detail.meeting.feedback || "—" }}</dd>
                        </template>
                    </dl>
                    <template v-if="detail.meeting.status !== 'cancelled'">
                        <div class="ui-divider"></div>
                        <div class="ui-label">{{ detail.meeting.status === "done" ? "Corrigir resultado" : "Registrar a agenda realizada" }}</div>
                        <v-row dense>
                            <v-col cols="12" sm="6"><v-select v-model="detail.result" :items="resultItems" label="Resultado" outlined dense clearable hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                            <v-col cols="12" sm="6"><v-text-field v-model="detail.done_at" type="date" label="Data em que ocorreu" outlined dense hide-details="auto"></v-text-field></v-col>
                            <v-col cols="12"><v-textarea v-model="detail.feedback" label="Feedback do juízo (vai para a ficha do juiz)" outlined dense rows="3" auto-grow hide-details="auto"></v-textarea></v-col>
                        </v-row>
                    </template>
                </div>
                <div class="ui-card__footer">
                    <v-btn v-if="detail.meeting.status === 'scheduled'" outlined class="ui-btn-danger" :disabled="busy" @click="cancelMeeting">Cancelar agenda</v-btn>
                    <v-btn v-if="(detail.meeting.status === 'scheduled') && $allowed('edit')" outlined :disabled="busy" @click="editMeeting">Editar</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn outlined @click="detail.open = false">Fechar</v-btn>
                    <v-btn v-if="(detail.meeting.status !== 'cancelled') && $allowed('save')" color="primary" depressed :loading="busy" @click="complete">
                        {{ detail.meeting.status === "done" ? "Salvar" : "Marcar como realizada" }}
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import ProspectMeetingDialog from "@/components/prospect/ProspectMeetingDialog.vue";
import {
    MEETING_RESULT_ITEMS, formatFeedDate, formatMoneyShort, formatShortDate, notifyError, notifySuccess, todayKey,
} from "@/components/prospect/prospect.util";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function addDays(date, days) {
    const value = new Date(`${date}T12:00:00Z`);
    value.setUTCDate(value.getUTCDate() + days);
    return value.toISOString().slice(0, 10);
}

export default {
    name: "ProspectMeetings",

    components: { ProspectMeetingDialog },

    data: () => ({
        data: null,
        history: [],
        date: todayKey(),
        today: todayKey(),
        busy: false,
        resultItems: MEETING_RESULT_ITEMS,
        dialog: { open: false, meeting: null },
        detail: { open: false, meeting: null, result: null, feedback: "", done_at: "" },
    }),

    computed: {
        // Segunda a sexta; sabado e domingo so quando tem algo marcado.
        visibleDays() {
            if (!this.data) return [];
            return this.data.days.filter((day, index) => index < 5 || day.meetings.length || day.deadlines.length);
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatFeedDate,
        formatMoneyShort,
        formatShortDate,

        weekday(date) {
            return WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()];
        },

        async load() {
            try {
                const [week, history] = await Promise.all([
                    axios.get("/api/admin/prospect/meetings/week", { params: { date: this.date } }),
                    axios.get("/api/admin/prospect/meetings/history"),
                ]);
                this.data = week.data.content;
                this.history = history.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        move(days) {
            this.date = addDays(this.date, days);
            this.load();
        },

        goToday() {
            this.date = todayKey();
            this.load();
        },

        openNew() {
            this.dialog = { open: true, meeting: null };
        },

        afterSave() {
            this.detail.open = false;
            this.load();
        },

        openDetail(meeting) {
            this.detail = { open: true, meeting, result: meeting.result, feedback: meeting.feedback || "", done_at: meeting.date };
        },

        editMeeting() {
            this.dialog = { open: true, meeting: this.detail.meeting };
        },

        async complete() {
            this.busy = true;
            try {
                await axios.post(`/api/admin/prospect/meetings/${this.detail.meeting.id}/complete`, { result: this.detail.result, feedback: this.detail.feedback, done_at: this.detail.done_at });
                notifySuccess(this, "Agenda registrada nos casos.");
                this.detail.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },

        async cancelMeeting() {
            const reason = await this.$dialog.prompt({ title: "Cancelar agenda", text: "Motivo (opcional, fica na linha do tempo dos casos)", actions: { false: "Voltar", true: "Cancelar agenda" } });
            if (reason === false || reason === undefined) return;
            this.busy = true;
            try {
                await axios.post(`/api/admin/prospect/meetings/${this.detail.meeting.id}/cancel`, { reason: reason || null });
                notifySuccess(this, "Agenda cancelada.");
                this.detail.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pm-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

.pm-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 18px;
    border-right: 1px solid $ui-border;

    &:last-child {
        border-right: 0;
    }
}

.pm-stat__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $ui-muted;
}

.pm-stat__value {
    font-size: 24px;
    font-weight: 600;
    color: $ui-fg;
}

.pm-stat__hint {
    font-size: 12px;
    color: $ui-muted;
}

.pm-week {
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    gap: 8px;

    @media (max-width: 960px) {
        grid-auto-flow: row;
    }
}

.pm-day {
    min-height: 180px;
    padding: 10px;
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;
}

.pm-day--today {
    border-color: $ui-accent;
    box-shadow: inset 0 2px 0 $ui-accent;
}

.pm-day__head {
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: $ui-fg;
}

.pm-card {
    display: block;
    width: 100%;
    margin-bottom: 8px;
    padding: 8px;
    text-align: left;
    border: 0;
    border-left: 3px solid $ui-accent;
    border-radius: $ui-radius-sm;
    background: $ui-accent-soft;
    cursor: pointer;

    &:hover {
        background: $ui-accent-bg;
    }
}

.pm-card--done {
    border-left-color: nth(map-get($ui-tones, success), 3);
    background: nth(map-get($ui-tones, success), 1);
}

.pm-card--cancelled {
    border-left-color: $ui-border-strong;
    background: $ui-bg-muted;
    opacity: 0.7;
}

.pm-card__time {
    font-size: 12px;
    font-weight: 600;
    color: $ui-accent;
}

.pm-card__title {
    font-size: 13px;
    font-weight: 600;
    color: $ui-fg;
}

.pm-card__trip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    max-width: 100%;
    white-space: nowrap;
    margin-top: 4px;
    padding: 0 6px;
    font-size: 11px;
    border-radius: 999px;
    color: #5b21b6;
    background: #f5f3ff;
}

.pm-card__trip-name {
    overflow: hidden;
    text-overflow: ellipsis;
}

.pm-card__meta {
    font-size: 12px;
    color: $ui-muted;
}

.pm-deadline {
    display: block;
    margin-bottom: 6px;
    padding: 6px 8px;
    font-size: 12px;
    border-radius: $ui-radius-sm;
    background: nth(map-get($ui-tones, warning), 1);
    color: nth(map-get($ui-tones, warning), 2);
    text-decoration: none;
}

.pm-empty {
    font-size: 12px;
    color: $ui-subtle;
}

.pm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 6px 8px;
        text-align: left;
        font-weight: 500;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 8px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }

    tbody tr {
        cursor: pointer;

        &:hover td {
            background: $ui-bg-muted;
        }
    }

    .r {
        text-align: right;
    }
}

.pm-feedback {
    max-width: 280px;
}

.pm-dl {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 8px 12px;
    margin: 0;
    font-size: 13px;

    dt {
        color: $ui-muted;
    }

    dd {
        margin: 0;
    }
}
</style>
